import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Chip,
  useMediaQuery,
  useTheme,
  Divider,
  CircularProgress
} from '@mui/material';
import { Images } from '../../utils/Images';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserTransactions } from "../../api/services/userService";

const DonateOnce = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery('(min-width:600px) and (max-width:900px)');
  const isSmallMediumScreen = useMediaQuery('(min-width:600px) and (max-width:800px)');
  const [showFilters, setShowFilters] = useState(false);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // iOS Safari does not show placeholder for input[type="date"], and can behave oddly unless toggled from text->date on focus
  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const getDateFieldType = (value) => (isIOS && !value ? "text" : "date");
  const handleDateFocus = (e) => {
    if (isIOS) e.target.type = "date";
  };
  const handleDateBlur = (e) => {
    if (isIOS && !e.target.value) e.target.type = "text";
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Format currency helper
  const formatCurrency = (amount, currency = "INR") => {
    if (!amount) return "₹0";
    const numAmount = parseFloat(amount);
    if (currency === "INR") {
      return `₹${numAmount.toLocaleString("en-IN")}`;
    }
    return `${currency} ${numAmount.toLocaleString("en-US")}`;
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        donationType: "payonce", // Filter by payonce
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }
      if (status) {
        // Dropdown says "completed" but the DB stores the enum as "success"
        params.status = status === "completed" ? "success" : status;
      }

      const response = await getUserTransactions(user.id, params);
      
      if (response?.success && response?.data) {
        // Filter and map API response to component format
        // DB stores the enum as "PayOnce" — compare case-insensitively
        const filteredData = response.data.filter(
          (transaction) => String(transaction.donationType || "").toLowerCase() === "payonce"
        );
        const mappedDonations = filteredData.map((transaction) => ({
          id: transaction.id,
          date: formatDate(transaction.createdAt),
          transactionId: transaction.paymentId || transaction.transactionId || `#${transaction.id}`,
          amount: formatCurrency(transaction.amount, transaction.currency),
          campaign: transaction.campaignName || "N/A",
          campaignId: transaction.campaignId || null,
          // Campaign page + donor's original UTMs + cutm_platform=p_donateonce (built by the API)
          campaignUrl: transaction.campaignUrl
            || (transaction.campaignId ? `/campaigns/${transaction.campaignId}?cutm_platform=p_donateonce` : null),
          status: transaction.status === "success" ? "Completed" : transaction.status === "failed" ? "Failed" : "Pending",
        }));
        
        setDonations(mappedDonations);
        setPagination(response.pagination || pagination);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setDonations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [user?.id, page, rowsPerPage, searchTerm, fromDate, toDate, status]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleBackToMenu = () => {
    navigate('/profile');
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
       {/* Mobile header with back button */}
                        {isMobile && (
                          <Box
                           onClick={handleBackToMenu}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "20px",
                            width: "100%",
                          }}>
                            <Box 
                             
                              sx={{ 
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="#1D1D1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </Box>
                            <Typography sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0%",
                              color: "#1D1D1B"
                            }}>
                              Donate Once
                            </Typography>
                          </Box>
                        )}
      {!isMobile && (
        <Paper elevation={2} sx={{ overflow: isMediumScreen ? 'auto' : 'visible', boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important" }}>
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              p: 3,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important", // Force shadow to always appear
            }}
          >
             {/* Header and Search Section */}
             <Box
               sx={{
                 display: "flex",
                 flexDirection: isSmallMediumScreen ? "column" : "row",
                 justifyContent: isSmallMediumScreen ? "flex-start" : "end",
                 alignItems: isSmallMediumScreen ? "flex-start" : "center",
        mb: "20px",
                 gap: isSmallMediumScreen ? "16px" : "16px",
                 minWidth: 0, // Allow shrinking
                 overflow: "hidden", // Prevent overflow
                 flexWrap: "wrap", // Allow wrapping
               }}
             >
               {/* First row - Header and Search (only on smaller medium screens) - HIDDEN */}
               {false && (
                 <Box
                   sx={{
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "center",
                     width: "100%",
        gap: "16px",
                   }}
                 >
        <Typography 
          component="h1" 
          sx={{ 
            fontWeight: 600,
                       fontStyle: "SemiBold",
                       fontSize: "22px",
                       lineHeight: "22.34px",
                       letterSpacing: "0%",
                       color: "#1D1D1B",
            flexShrink: 0,
            margin: 0,
                       padding: 0,
          }}
        >
          Donate Once
        </Typography>

                   {/* Search field for medium screens */}
                   <TextField
                     placeholder="Search by Transaction Id"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     variant="outlined"
                     sx={{
                       minWidth: "200px",
                       maxWidth: "250px",
                       flexGrow: 1,
                       "& .MuiOutlinedInput-root": {
                         borderRadius: "8px",
                         paddingTop: "10px",
                         paddingRight: "20px",
                         paddingBottom: "10px",
                         paddingLeft: "20px",
                         backgroundColor: "#FFFFFF",
                         border: "1px solid #BEBFD0",
                         "& fieldset": {
                           border: "none",
                         },
                         "&:hover fieldset": {
                           border: "none",
                         },
                         "&.Mui-focused fieldset": {
                           border: "none",
                         },
                       },
                       "& .MuiInputBase-input": {
                         padding: 0,
                         margin: 0,
                         fontWeight: 500,
                         fontStyle: "Medium",
                         fontSize: "13px",
                         lineHeight: "100%",
                         letterSpacing: "0%",
                         verticalAlign: "middle",
                         color: "#686866",
                         "&::placeholder": {
                           fontWeight: 500,
                           fontStyle: "Medium",
                           fontSize: "13px",
                           lineHeight: "100%",
                           letterSpacing: "0%",
                           verticalAlign: "middle",
                           color: "#686866",
                           opacity: 1,
                         },
                       },
                     }}
                   />
                 </Box>
               )}

               {/* Header for desktop and larger medium screens */}
               {!isSmallMediumScreen && (
                 <Typography
                   component="h1"
                   sx={{
                     fontWeight: 600,
                     fontStyle: "SemiBold",
                     fontSize: "22px",
                     lineHeight: "22.34px",
                     letterSpacing: "0%",
                     color: "#1D1D1B",
                     flexShrink: 0,
                     margin: 0,
                     padding: 0,
                     order: 1, // Always stay on the left
                   }}
                 >
                   Donate Once
                 </Typography>
               )}

               {/* Search Field - Top Right */}
               {!isSmallMediumScreen && (
        <Box sx={{ 
                  flexShrink: 0,
                  order: 2, // Always stay on the right
                  marginLeft: "auto",
                  flex: "0 1 auto", // Don't grow by default
                  maxWidth: "300px", // Limit default width
                  '@media (max-width: 1250px)': {
                    flex: "1 1 auto", // Fill space when filters come down
                    maxWidth: "100%",
                  },
                }}>
          <TextField
            placeholder="Search by Transaction Id"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            sx={{
                      width: "100%", // Fill the container
                      minWidth: "220px",
                      '@media (min-width: 1250px)': {
                        maxWidth: "300px", // Limit width on large screens
                      },
                      '@media (max-width: 1250px)': {
                        maxWidth: "none", // No limit when filters come down
                      },
                       "& .MuiOutlinedInput-root": {
                         borderRadius: "8px",
                         paddingTop: "14px",
                         paddingRight: "24px",
                         paddingBottom: "14px",
                         paddingLeft: "24px",
                         backgroundColor: "#FFFFFF",
                         border: "1px solid #BEBFD0",
                         "& fieldset": {
                           border: "none",
                         },
                         "&:hover fieldset": {
                           border: "none",
                         },
                         "&.Mui-focused fieldset": {
                           border: "none",
                         },
                       },
                       "& .MuiInputBase-input": {
                padding: 0,
                margin: 0,
                fontWeight: 500,
                         fontStyle: "Medium",
                         fontSize: "14px",
                         lineHeight: "100%",
                         letterSpacing: "0%",
                         verticalAlign: "middle",
                         color: "#686866",
                         "&::placeholder": {
                  fontWeight: 500,
                           fontStyle: "Medium",
                           fontSize: "14px",
                           lineHeight: "100%",
                           letterSpacing: "0%",
                           verticalAlign: "middle",
                           color: "#686866",
                  opacity: 1,
                },
              },
            }}
          />
                 </Box>
               )}

               {/* Date Fields and Status Filter - Separate Box */}
               {!isSmallMediumScreen && (
                 <Box
                   sx={{
                     display: "flex",
                     gap: "8px",
                     alignItems: "center",
                     flexWrap: "nowrap", // Keep them together
                     flexShrink: 0, // Don't allow this box to shrink
                     order: 3, // Will come after search
                   }}
                 >
          {/* From Date */}
          <TextField
            type={getDateFieldType(fromDate)}
            placeholder="From date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
            variant="outlined"
            sx={{
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
              "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      paddingTop: isMediumScreen ? "10px" : "13px",
                      paddingRight: isMediumScreen ? "16px" : "24px",
                      paddingBottom: isMediumScreen ? "10px" : "13px",
                      paddingLeft: isMediumScreen ? "16px" : "24px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #BEBFD0",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                padding: 0,
                margin: 0,
                fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#686866",
                      "&::placeholder": {
                  fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                  opacity: 1,
                },
              },
                    "& .MuiInputLabel-root": {
                      display: "none",
              },
            }}
          />

          {/* To Date */}
          <TextField
            type={getDateFieldType(toDate)}
            placeholder="To date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
            variant="outlined"
            sx={{
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
                      "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      paddingTop: isMediumScreen ? "10px" : "13px",
                      paddingRight: isMediumScreen ? "16px" : "24px",
                      paddingBottom: isMediumScreen ? "10px" : "13px",
                      paddingLeft: isMediumScreen ? "16px" : "24px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #BEBFD0",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                padding: 0,
                margin: 0,
                fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#686866",
                      "&::placeholder": {
                  fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                  opacity: 1,
                },
              },
                    "& .MuiInputLabel-root": {
                      display: "none",
              },
            }}
          />

          {/* Status Filter */}
                <FormControl sx={{ 
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
                   }}>
            <Select
              value={status}
              displayEmpty
              onChange={(e) => setStatus(e.target.value)}
              sx={{
                         borderRadius: "8px",
                         paddingTop: isMediumScreen ? "10px" : "15px",
                         paddingRight: isMediumScreen ? "16px" : "24px",
                         paddingBottom: isMediumScreen ? "10px" : "9px",
                         paddingLeft: isMediumScreen ? "16px" : "24px",
                         backgroundColor: "#FFFFFF",
                         border: "1px solid #BEBFD0",
                         "& .MuiOutlinedInput-notchedOutline": {
                           border: "none",
                         },
                         "&:hover .MuiOutlinedInput-notchedOutline": {
                           border: "none",
                         },
                         "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                           border: "none",
                         },
                         "& .MuiSelect-select": {
                  padding: 0,
                  margin: 0,
                  fontWeight: 500,
                           fontStyle: "Medium",
                           fontSize: "14px",
                           lineHeight: "100%",
                           letterSpacing: "0%",
                           verticalAlign: "middle",
                           color: status ? "#1D1D1B" : "#686866",
                         },
                         "& .MuiSelect-icon": {
                           color: "#686866",
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                           return <span style={{ color: "#686866" }}>Status</span>;
                }
                return selected;
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
               )}

               {/* Header for 800px to 600px screens with filter button */}
               {isSmallMediumScreen && (
                 <Box sx={{ 
                   display: "flex", 
                   justifyContent: "space-between", 
                   alignItems: "center", 
                   width: "100%",
                   order: 1,
                 }}>
                   <Typography
                     component="h1"
                     sx={{
                       fontWeight: 600,
                       fontStyle: "SemiBold",
                       fontSize: "22px",
                       lineHeight: "22.34px",
                       letterSpacing: "0%",
                       color: "#1D1D1B",
                       flexShrink: 0,
                       margin: 0,
                       padding: 0,
                     }}
                   >
                     Donate Once
                   </Typography>
                   <IconButton
                     onClick={() => setShowFilters(!showFilters)}
                     sx={{
                       backgroundColor: "#1D1D1B !important",
                       borderRadius: "8px",
                       width: "40px",
                       height: "40px",
                     }}
                   >
                     <Box
                       component="img"
                       src={showFilters ? Images.whiteClose : Images.filter}
                       alt={showFilters ? "Close" : "Filter"}
                       sx={{ width: "20px", height: "20px" }}
                     />
                   </IconButton>
                 </Box>
               )}

               {/* Filter Section for 800px to 600px screens */}
               {isSmallMediumScreen && showFilters && (
                 <Box
                   sx={{
                     order: 2,
                     width: "100%",
                     display: "flex",
                     flexDirection: "column",
                     gap: "12px",
                   }}
                 >
                   <TextField
                     placeholder="Search By Transaction ID"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     variant="outlined"
                     sx={{
                       width: "100%",
                       "& .MuiOutlinedInput-root": {
                         borderRadius: "8px",
                         paddingTop: "10px",
                         paddingRight: "16px",
                         paddingBottom: "10px",
                         paddingLeft: "16px",
                         backgroundColor: "#FFFFFF",
                         border: "1px solid #BEBFD0",
                         "& fieldset": {
                           border: "none",
                         },
                       },
                     }}
                     InputProps={{
                       startAdornment: (
                         <InputAdornment position="start">
                           <Box
                             component="img"
                             src={Images.search}
                             alt="Search"
                             sx={{
                               width: "16px",
                               height: "16px",
                             }}
                           />
                         </InputAdornment>
                       ),
                     }}
                   />
                   <Box
                     sx={{
                       display: "flex",
                       gap: "8px",
                       flexWrap: "wrap",
                     }}
                   >
                     <TextField
                       type={getDateFieldType(fromDate)}
                       placeholder="From date"
                       value={fromDate}
                       onChange={(e) => setFromDate(e.target.value)}
                       onFocus={handleDateFocus}
                       onBlur={handleDateBlur}
                       variant="outlined"
                       sx={{
                         flex: "1",
                         minWidth: "140px",
                         "& .MuiOutlinedInput-root": {
                           height: "40px",
                           borderRadius: "8px",
                           border: "1px solid #BEBFD0",
                           "& fieldset": {
                             border: "none",
                           },
                         },
                       }}
                     />
                     <TextField
                       type={getDateFieldType(toDate)}
                       placeholder="To date"
                       value={toDate}
                       onChange={(e) => setToDate(e.target.value)}
                       onFocus={handleDateFocus}
                       onBlur={handleDateBlur}
                       variant="outlined"
                       sx={{
                         flex: "1",
                         minWidth: "140px",
                         "& .MuiOutlinedInput-root": {
                           height: "40px",
                           borderRadius: "8px",
                           border: "1px solid #BEBFD0",
                           "& fieldset": {
                             border: "none",
                           },
                         },
                       }}
                     />
                     <FormControl sx={{ flex: "1", minWidth: "140px" }}>
                       <Select
                         value={status}
                         displayEmpty
                         onChange={(e) => setStatus(e.target.value)}
                         sx={{
                           height: "40px",
                           borderRadius: "8px",
                           backgroundColor: "#FFFFFF",
                           border: "1px solid #BEBFD0",
                           "& .MuiOutlinedInput-notchedOutline": {
                             border: "none",
                           },
                           "& .MuiSelect-select": {
                             paddingLeft: "16px",
                           },
                         }}
                         renderValue={(selected) => {
                           if (!selected) {
                             return <span style={{ color: "#686866" }}>Status</span>;
                           }
                           return selected;
                         }}
                       >
                         <MenuItem value="">All</MenuItem>
                         <MenuItem value="completed">Completed</MenuItem>
                         <MenuItem value="pending">Pending</MenuItem>
                         <MenuItem value="failed">Failed</MenuItem>
                       </Select>
                     </FormControl>
                   </Box>
                 </Box>
               )}
             </Box>

              {/* Table - Desktop/Tablet View */}
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "4px",
                  boxShadow: "none",
                  border: "none",
                  overflowX: "auto", // Enable horizontal scrolling
                  maxWidth: "100%",
                  "& .MuiTableCell-root": {
                    padding: "16px",
                    verticalAlign: "middle",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                <Table sx={{ width: "max-content", minWidth: "100%" }}>
          <TableHead>
                    <TableRow
                      sx={{ backgroundColor: "#F5F8ED", height: "60px" }}
                    >
                      <TableCell
                        sx={{
                fontWeight: 600,
                          fontStyle: "SemiBold",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          borderBottom: "1px solid #E0E0E0",
                          whiteSpace: "nowrap",
                        }}
                      >
                Donation Date
              </TableCell>
                      <TableCell
                        sx={{
                fontWeight: 600,
                          fontStyle: "SemiBold",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          borderBottom: "1px solid #E0E0E0",
                          whiteSpace: "nowrap",
                        }}
                      >
                Transaction ID
              </TableCell>
                      <TableCell
                        sx={{
                fontWeight: 600,
                          fontStyle: "SemiBold",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          borderBottom: "1px solid #E0E0E0",
                        }}
                      >
                Amount
              </TableCell>
                      <TableCell
                        sx={{
                fontWeight: 600,
                          fontStyle: "SemiBold",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          borderBottom: "1px solid #E0E0E0",
                        }}
                      >
                Status
              </TableCell>
                      <TableCell
                        sx={{
                fontWeight: 600,
                          fontStyle: "SemiBold",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          borderBottom: "1px solid #E0E0E0",
                        }}
                      >
                Campaign
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                    {donations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography sx={{ color: "#686866" }}>No transactions found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                    donations.map((donation) => (
                        <TableRow
                          key={donation.id}
                          sx={{
                            "&:hover": { backgroundColor: "#F8F9FA" },
                            "&:last-child td": { borderBottom: 0 },
                            height: "60px",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "Red Hat Display",
                  fontWeight: 500,
                              fontStyle: "Medium",
                              fontSize: "16px",
                              lineHeight: "16px",
                              letterSpacing: "0%",
                              color: "#1D1D1B",
                              whiteSpace: "nowrap",
                            }}
                          >
                  {donation.date}
                </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Red Hat Display",
                  fontWeight: 500,
                              fontStyle: "Medium",
                              fontSize: "16px",
                              lineHeight: "16px",
                              letterSpacing: "0%",
                              color: "#1D1D1B",
                              whiteSpace: "nowrap",
                            }}
                          >
                  {donation.transactionId}
                </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Red Hat Display",
                  fontWeight: 500,
                              fontStyle: "Medium",
                              fontSize: "16px",
                              lineHeight: "16px",
                              letterSpacing: "0%",
                              color: "#1D1D1B",
                            }}
                          >
                  {donation.amount}
                </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Box
                              sx={{
                                width: "89px",
                                padding: "6px 0px",
                                textAlign: "center",
                                borderRadius: "50.198px",
                                color: "#fff",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor:
                                  donation.status === "Completed"
                                    ? "#29CC6A"
                                    : donation.status === "Pending"
                                    ? "#EEAE3F"
                                    : donation.status === "Failed"
                                    ? "#FF5858"
                                    : "transparent",
                              }}
                            >
                              {donation.status}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Red Hat Display",
                  fontWeight: 500,
                              fontStyle: "Medium",
                              fontSize: "16px",
                              lineHeight: "16px",
                              letterSpacing: "0%",
                              color: "#1D1D1B",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "300px",
                              width: "300px",
                            }}
                          >
                  {donation.campaignId ? (
                    <a
                      href={donation.campaignUrl}
                      style={{
                        color: "#8EA946",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                      onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                    >
                      {donation.campaign}
                    </a>
                  ) : (
                    donation.campaign
                  )}
                </TableCell>
                        </TableRow>
                      )))}
                  </TableBody>
                </Table>
                )}
              </TableContainer>

              {/* Pagination */}
              <Box
                      sx={{ 
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  flexWrap: "nowrap", // Main container won't wrap
                  gap: 2,
                      }}
                    >
                      <Box 
                        sx={{ 
                    // Left side: Rows per page + Row count
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap", // Allows internal wrapping
                    minWidth: 0,
                    mr: 2, // Margin to separate from pagination buttons
                      }}
                    >
                      <Box 
                        sx={{ 
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0, // Don't shrink this
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#5E5E5E", whiteSpace: "nowrap" }}
                    >
            Rows per page:
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{ 
                        minWidth: "60px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E0E0E0",
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#5E5E5E", whiteSpace: "nowrap", flexShrink: 1 }}
                  >
            Row {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, pagination.totalRecords)} of {pagination.totalRecords} rows
          </Typography>
        </Box>
                <Box
                  sx={{
                    // Right side: Pagination buttons
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "nowrap", // Buttons stay together
                    flexShrink: 0, // Never shrink this
                  }}
                >
          {/* Back Button */}
          <Box
                    onClick={() => page > 0 && setPage(page - 1)}
            sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#FFFFFF",
                      cursor: page === 0 ? "not-allowed" : "pointer",
              opacity: page === 0 ? 0.5 : 1,
                      "&:hover": {
                        backgroundColor: page === 0 ? "#FFFFFF" : "#F5F5F5",
              },
            }}
          >
            {/* Left Chevron SVG */}
            <Box
              sx={{
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="#5E5E5E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                        color: "#5E5E5E",
                fontWeight: 500,
                        fontSize: "14px",
              }}
            >
              Back
            </Typography>
          </Box>

          {/* Page Numbers */}
          {(() => {
            const totalPages = Math.max(1, pagination.totalPages || 1);
            const currentPage = pagination.currentPage || 1;
            
            // Generate page numbers to display
            const getPageNumbers = () => {
              if (totalPages === 0) return [1];
              if (totalPages === 1) return [1];
              
              const pages = [];
              const currentPageIndex = currentPage - 1; // Convert to 0-based
              
              if (currentPageIndex === 0) {
                // On first page, show up to 3 pages
                for (let i = 1; i <= Math.min(3, totalPages); i++) {
                  pages.push(i);
                }
              } else if (currentPageIndex === totalPages - 1) {
                // On last page, show previous pages
                const start = Math.max(1, totalPages - 2);
                for (let i = start; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // On middle pages, show current and previous pages
                const start = Math.max(1, currentPageIndex);
                for (let i = start; i <= Math.min(start + 2, totalPages); i++) {
                  pages.push(i);
                }
              }
              return pages;
            };
            
            return getPageNumbers().map((pageNum) => {
              const pageIndex = pageNum - 1; // Convert to 0-based
              return (
                <Box
                  key={pageNum}
                  onClick={() => setPage(pageIndex)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    backgroundColor:
                      page === pageIndex ? "#1D1D1B" : "#FFFFFF",
                    border:
                      page === pageIndex ? "none" : "1px solid #E0E0E0",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        page === pageIndex ? "#2A2A2A" : "#F5F5F5",
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: "14px",
                      color: page === pageIndex ? "#FFFFFF" : "#5E5E5E",
                    }}
                  >
                    {pageNum}
                  </Typography>
                </Box>
              );
            });
          })()}

          {/* Next Button */}
          <Box
                    onClick={() => {
                      const totalPages = Math.max(1, pagination.totalPages || 1);
                      if (page < totalPages - 1) setPage(page + 1);
                    }}
            sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#FFFFFF",
                      cursor: page >= (pagination.totalPages || 1) - 1 ? "not-allowed" : "pointer",
              opacity: page >= (pagination.totalPages || 1) - 1 ? 0.5 : 1,
                      "&:hover": {
                        backgroundColor: !pagination.hasNextPage ? "#FFFFFF" : "#F5F5F5",
              },
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                        color: "#5E5E5E",
                fontWeight: 500,
                        fontSize: "14px",
              }}
            >
              Next
            </Typography>
            {/* Right Chevron SVG */}
            <Box
              sx={{
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="#5E5E5E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>
            </Box>
        </Paper>
      )}

      {/* Mobile View */}
      {isMobile && (
        <Box>
          {/* Mobile Search and Filter */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mb: "20px",
            }}
          >
            <TextField
              placeholder="Search By Transaction ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #DDD",
                  padding: "12px 20px 12px 16px",
                  paddingRight: "0px",
                  height: "40px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#686866",
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      src={Images.search}
                      alt="Search"
                      sx={{ width: "18px", height: "18px" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: "#1D1D1B !important",
                borderRadius: "8px",
                width: "40px",
                height: "40px",
              }}
            >
              <Box
                component="img"
                src={showFilters ? Images.whiteClose : Images.filter}
                alt={showFilters ? "Close" : "Filter"}
                sx={{ width: "20px", height: "20px" }}
              />
            </IconButton>
          </Box>
          
          {/* Filter Section for Mobile */}
          {showFilters && (
            <Box sx={{ 
              mb: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}>
              <Box sx={{ 
                display: "flex", 
                gap: "8px", 
                flexWrap: "wrap",
              }}>
                <TextField
                  type={getDateFieldType(fromDate)}
                  placeholder="From date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <TextField
                  type={getDateFieldType(toDate)}
                  placeholder="To date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <FormControl sx={{ 
                  flex: "1",
                  minWidth: "140px",
                }}>
                  <Select
                    value={status}
                    displayEmpty
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      height: "40px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        fontSize: "14px",
                        color: status ? "#1D1D1B" : "#686866",
                        padding: "12px 16px",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#686866" }}>Status</span>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Donation Cards - Mobile View */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
              <CircularProgress />
            </Box>
          ) : donations.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "#686866" }}>No transactions found</Typography>
            </Box>
          ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {donations.map((donation) => (
                <Box
                  key={donation.id}
                  sx={{
                    border: "1px solid rgba(0, 0, 0, 0.10)",
                    borderRadius: "16px",
                    padding: "16px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {/* Transaction ID + Status */}
                  <Box sx={{ backgroundColor: "#F5F8ED", padding: "8px 16px", borderRadius: "8px", mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#8EA946",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Transaction ID - <span style={{color: "#1D1D1B", fontWeight: 700,}}>{donation.transactionId}</span>
                    </Typography>
                    <Box
                      sx={{
                        padding: "4px 12px",
                        borderRadius: "50.198px",
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: 600,
                        flexShrink: 0,
                        backgroundColor:
                          donation.status === "Completed"
                            ? "#29CC6A"
                            : donation.status === "Pending"
                            ? "#EEAE3F"
                            : donation.status === "Failed"
                            ? "#FF5858"
                            : "transparent",
                      }}
                    >
                      {donation.status}
                    </Box>
                  </Box>

                  {/* Campaign */}
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#686866",
                      mb: 1,
                    }}
                  >
                    Campaign
                  </Typography>
                  {donation.campaignId ? (
                    <a
                      href={donation.campaignUrl}
                      style={{
                        textDecoration: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#8EA946",
                          mb: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {donation.campaign}
                      </Typography>
                    </a>
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#1D1D1B",
                        mb: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {donation.campaign}
                    </Typography>
                  )}

                  {/* Divider */}
                  <Divider sx={{ mb: 2 }} />

                  {/* Donation Date and Amount */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "12px",
                          color: "#5E5E5E",
                        }}
                      >
                        Donation Date
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#1D1D1B",
                          mt: 0.5,
                        }}
                      >
                        {donation.date}
                      </Typography>
                    </Box>
                    
                    {/* Vertical Divider */}
                    <Box sx={{ width: "1px", height: "40px", backgroundColor: "#E0E0E0", mx: 2 }} />
                    
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "12px",
                          color: "#5E5E5E",
                        }}
                      >
                        Amount Paid
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#29CC6A",
                          mt: 0.5,
                        }}
                      >
                        {donation.amount}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>
          )}

          {/* Mobile Pagination */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            alignItems: 'center',
            gap: 2
          }}>
            {/* Back Button */}
            <Box
              onClick={() => page > 0 && setPage(Math.max(0, page - 1))}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor: !pagination.hasPrevPage ? "not-allowed" : "pointer",
                opacity: !pagination.hasPrevPage ? 0.5 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12L6 8L10 4" stroke="#5E5E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>

            {/* Page Indicator */}
            <Typography sx={{ 
              fontSize: '14px',
              fontWeight: 500,
              color: '#1D1D1B'
            }}>
              {pagination.totalPages === 0 ? 0 : page + 1} / {Math.max(1, pagination.totalPages || 1)}
            </Typography>

            {/* Next Button */}
            <Box
              onClick={() => {
                const totalPages = Math.max(1, pagination.totalPages || 1);
                if (page < totalPages - 1) setPage(page + 1);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor: page >= (pagination.totalPages || 1) - 1 ? "not-allowed" : "pointer",
                opacity: page >= (pagination.totalPages || 1) - 1 ? 0.5 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="#5E5E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DonateOnce;